import React, { useState, useRef, useEffect } from 'react';
import ChrCalendar from '../Basic/ChrCalendar';
import ChrText from '../Basic/ChrText';
import { safeMutate } from '../../../../utils/GeneralUtils';
import { getUId, propagateData } from '../utils';
import { ChrDatePickerTemplates } from '../../../specific/Group/templates/Filters/SelectAndDatePickerTemplate';
import i18n from '../../../../utils/ChrI18n';
import useToggler from '../../../../hooks/generic/Utils/useToggler';
import { DATE_STRING_OPTIONS } from '../../../../utils/Constants';
import { createPortal } from 'react-dom';

/*

props = {
  data: {
    value: <>,
    placeholder: <>,
    defaultValue: <>,
    className: <>,
    disabled: <>,
    locale: <>,
    textBoxClass: <>,
    calendarClass: <>,
    bufferOffset: <>,
    rightOffset: <>
  },
  uId: <uniqueIdentifier>,
  onChange: <handler>,
  controlComponentData: {}
}

*/

const ChrDatePicker = (props) => {
  const formData = props.data;
  const myUId = getUId(props);
  const dateObj = formData.value ? new Date(formData.value) : formData.value;
  const currentLocale = formData.locale || i18n.locale;
  const dateInputRef = useRef(null);
  const calendarContainerRef = useRef(null);
  const { showContent, showContentToggler, contentRef } = useToggler({ extraClickableContentRef: calendarContainerRef, useCapture: true });

  const calendarWrapperComponent = useRef(null);
  const [styleForCalendar, setStyleForCalendar] = useState();

  // TODO_CONNECTION_VIEWS: fix auto close on clicking delete icon in MultiInput
  const reactCalendarOnClickHandler = event => {
    event.stopPropagation();
    calendarWrapperComponent.current && calendarWrapperComponent.current.click();
  };

  let selectedDateString = "";

  if (dateObj instanceof Date) {
    selectedDateString = dateObj.toLocaleDateString(currentLocale, formData.format || {
      year: DATE_STRING_OPTIONS.NUMERIC,
      month: DATE_STRING_OPTIONS.LONG,
      day: DATE_STRING_OPTIONS.NUMERIC
    });
  }

  // closing the open calendar by default if we disable the component
  if (formData.disabled && showContent) {
    showContentToggler();
  }

  // Focus management for calendar
  useEffect(() => {
    if (showContent && calendarContainerRef.current) {
      calendarContainerRef.current.focus();
    }
  }, [showContent]);

  const getMinMaxDateObj = () => {
    const minDateObj = formData.minDate ? new Date(formData.minDate) : formData.minDate;
    const maxDateObj = formData.maxDate ? new Date(formData.maxDate) : formData.maxDate;

    return {
      minDate: minDateObj,
      maxDate: maxDateObj
    };
  };

  const onChangeHandler = (event, _childUId, childData) => {
    const newFormData = safeMutate(formData, draftFormData => {
      const date = childData.value;
      if (date instanceof Date) {
        draftFormData.value = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
      } else {
        draftFormData.value = date;
      }
    });
    toggleCalendar(null, null, null);
    propagateData(event, myUId, newFormData, props);
    if(dateInputRef.current)
      dateInputRef.current.focus();
  };

  const toggleCalendar = (_event, _childUId, _childData) => {
    setStyleForCalendar(getPositionStyleForCalendar());
    !formData.disabled && showContentToggler();
  };

  const getPositionStyleForCalendar = () => {
    if (dateInputRef && dateInputRef.current) {
      const boundingClientRect = dateInputRef.current.getBoundingClientRect();
      const bufferOffset = formData.useInputHeightAsVerticalOffset ? boundingClientRect.height : (formData.bufferOffset || 50);
      const verticalGap = formData.verticalGap || 0;
      const top = boundingClientRect.top + window.scrollY + bufferOffset + verticalGap;
      const right = (window.innerWidth - boundingClientRect.right) - (formData.rightOffset || 0);
      return formData.alignCalendarRight ? { top: top, right: right } : { top: top, left: boundingClientRect.left };
    }
    else
      return {}
  }


  const handleOnCalendarKeyDown = (event) => {
    if (event.key === 'Escape') {
      showContentToggler();
    }
  };

  const textBoxDisplayClass = formData.textBoxClass || ChrDatePickerTemplates.OverflowTemplate.textBoxClass;
  const calendarDisplayClass = formData.calendarClass || ChrDatePickerTemplates.OverflowTemplate.calendarClass;
  const paddingClass = props.paddingClass || "py-2"

  const DefaultControlComponent = (
    <div className={ `cui-date-picker-react d-flex flex-row ${paddingClass} ${textBoxDisplayClass}` } ref={ dateInputRef } >
      <div className="gray-bg pt-2 px-2 border cui-calendar-icon-container">
        <i className="fa-regular fa-calendar-days m-t-2 m-r-0 fa-fw m-r-xs"></i>
      </div>
      <ChrText formGroupClass={"no-margins flex-grow-1"} data={{ placeholder: formData.placeholder, value: selectedDateString, readOnly: formData.readOnly || formData.disabled, disabled: formData.disabled }} onClick={toggleCalendar} formControlClass={props.formControlClass || "cjs-date-picker-input"} ariaLabel={props.ariaTextLabel} />
    </div>
  );

  return (
    <React.Fragment>
      <div ref={contentRef} {...props.containerProps}>
        {formData.controlComponent ? <formData.controlComponent onClick={toggleCalendar} inputRef={dateInputRef} data={props.controlComponentData} /> : DefaultControlComponent}
        <div ref={calendarWrapperComponent}>
          <div onClick={reactCalendarOnClickHandler}>
            {showContent && createPortal(
              <div
                className={`${calendarDisplayClass} custom-width-18 cui-react-date-picker-wrapper`}
                style={styleForCalendar}
                ref={calendarContainerRef}
                tabIndex={-1}
                onKeyDown={handleOnCalendarKeyDown}
              >
                <ChrCalendar
                  onChange={ onChangeHandler }
                  data={ {
                    value: dateObj,
                    defaultValue: formData.defaultValue,
                    locale: currentLocale,
                    skipFooter: formData.skipFooter,
                    ...getMinMaxDateObj(),
                  } }
                />
              </div>, document.body) }
          </div>
        </div>
      </div>

    </React.Fragment>
  );
};

export default ChrDatePicker;
